import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/http';

export type CertificateStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';

export interface KsefCertificate {
  id: number;
  nip: string;
  serialNumber: string;
  subject: string;
  validFrom: string; // ISO date-time
  validUntil: string;
  status: CertificateStatus;
}

export interface AddCertificateInput {
  nip: string;
  certificateFile: File; // PEM .crt/.pem
  keyFile: File; // PEM .key/.pem
  password?: string; // only if the key is encrypted
}

export const CERTIFICATES_QUERY_KEY = ['certificates'] as const;

export function useCertificates() {
  return useQuery<KsefCertificate[]>({
    queryKey: CERTIFICATES_QUERY_KEY,
    queryFn: () => request<KsefCertificate[]>('/api/v1/ksef/certificates'),
    staleTime: 60_000,
  });
}

export function useAddCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddCertificateInput) => {
      const body = {
        nip: input.nip,
        certificatePem: await input.certificateFile.text(),
        privateKeyPem: await input.keyFile.text(),
        password: input.password || undefined,
      };
      return request<KsefCertificate>('/api/v1/ksef/certificates', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CERTIFICATES_QUERY_KEY }),
  });
}

export function useRevokeCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      request<void>(`/api/v1/ksef/certificates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CERTIFICATES_QUERY_KEY }),
  });
}
